import React, { useRef } from "react";
import { toast } from "sonner";
import Conversation from "@/model/interface/Conversation";
import { useMessageDialog } from "@/context/MessageDialogContext";
import { useSendMessageMutation, usePostFileMutation, useGetSmsEligibilityQuery } from "@/store/rest_api";
import OutboundGuardDialogs from "@/components/compliance/OutboundGuardDialogs";
import { useOutboundGuard } from "@/hooks/useOutboundGuard";
import SmsBlockedNotice from "@/components/messages/SmsBlockedNotice";
import DoNotTextNotice from "@/components/messages/DoNotTextNotice";
import { useTextBlockingEntry } from "@/hooks/useTextBlockingEntry";
import { isSmsSendable } from "@/util/a2p";
import { Button } from "@/components/ui/button";
import { Send, Paperclip, Loader2, X } from "lucide-react";

interface MessageComposerProps {
  conversation: Conversation;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
}

const MessageComposer: React.FC<MessageComposerProps> = ({ conversation, inputRef }) => {
  const { threadDraft, setThreadDraft } = useMessageDialog();
  const { body, mediaUrls } = threadDraft(conversation.id);
  const setBody = (value: string) =>
    setThreadDraft(conversation.id, (current) => ({ ...current, body: value }));
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [uploadFile, { isLoading: isUploading }] = usePostFileMutation();
  const outboundGuard = useOutboundGuard();
  const { data: smsEligibility } = useGetSmsEligibilityQuery();
  const textBlockingEntry = useTextBlockingEntry(
    conversation.participants.map((participant) => participant.phoneNumber),
  );
  const ownTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef ?? ownTextareaRef;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toNumbers = conversation.participants.map((p) => p.phoneNumber);
  const sendBlocked =
    (!!smsEligibility && !isSmsSendable(conversation.fromPhoneNumber, smsEligibility.orgApproved)) ||
    !!textBlockingEntry;

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed && mediaUrls.length === 0) return;

    try {
      // The server checks every participant of a group thread, not just the first.
      await outboundGuard.guard(async (options) => {
        await sendMessage({
          fromPhoneNumber: { id: conversation.fromPhoneNumber.id },
          toNumbers,
          body: trimmed || undefined,
          mediaUrls: mediaUrls.length > 0 ? mediaUrls : undefined,
          ...options,
        }).unwrap();

        setThreadDraft(conversation.id, () => ({ body: "", mediaUrls: [] }));
        textareaRef.current?.focus();
      });
    } catch (e: any) {
      toast.error(e?.detail || "Failed to send message");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await uploadFile({ file }).unwrap();
      if (result?.url) {
        setThreadDraft(conversation.id, (current) => ({
          ...current,
          mediaUrls: [...current.mediaUrls, result.url],
        }));
      }
    } catch (err) {
      console.error("Failed to upload file", err);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeMedia = (index: number) => {
    setThreadDraft(conversation.id, (current) => ({
      ...current,
      mediaUrls: current.mediaUrls.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="shrink-0 border-t border-border p-3">
      <OutboundGuardDialogs guard={outboundGuard} actionLabel="Send anyway" />
      <SmsBlockedNotice fromPhoneNumber={conversation.fromPhoneNumber} className="mb-2" />
      <DoNotTextNotice
        blockingEntry={textBlockingEntry}
        showNumber={conversation.participants.length > 1}
        className="mb-2"
      />
      {mediaUrls.length > 0 && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {mediaUrls.map((url, i) => (
            <div key={i} className="relative">
              <img src={url} alt="attachment" className="h-16 w-16 object-cover rounded" />
              <button
                onClick={() => removeMedia(i)}
                className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full h-4 w-4 flex items-center justify-center"
                title="Remove attachment"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || !!textBlockingEntry}
          title="Attach a file"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Paperclip className="h-4 w-4" />
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,application/pdf"
          className="hidden"
          onChange={handleFileSelect}
        />
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Text message"
          disabled={!!textBlockingEntry}
          rows={1}
          className="flex-1 resize-none rounded-full border border-input bg-background px-3.5 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <Button
          size="icon"
          className="h-8 w-8 shrink-0 rounded-full"
          onClick={handleSend}
          disabled={isSending || sendBlocked || (!body.trim() && mediaUrls.length === 0)}
          title="Send"
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default MessageComposer;
